import { Model } from 'es6/components/model'
import $ from 'init/jquery'
import isNumber from 'lodash/isNumber'

/**
 * Функциональность реакции на попадание в поле зрения пользователя.
 */
export const InViewPortMixin = {
  /**
   * @inheritDoc
   */
  afterRun () {
    this.cache = {}
    /*
     * модель по которой отслеживаем изменение попадания во viewport
     * изначально считаем что объект не попадает во viewport
     */
    this.viewPortModel = new Model({ in: false })
    // реагируем на скролл
    this.listenTo(this.mainWindow, 'window.scroll.throttled', this.showListening.bind(this))
    // реагируем на измерение размеров окна
    this.listenTo(this.mainWindow, 'window.resize.debounced', this.onResize.bind(this))
    // реагирует на окончание загрузки
    this.listenTo(this.mainWindow, 'window.load', this.showListening.bind(this))
    // состояние попадания во viewport изменилось
    this.listenTo(this.viewPortModel, 'change:in', this.changeViewport)
  },

  /**
   * Прекращает слушать события окна браузера.
   */
  stopViewportListening () {
    this.stopListening(this.mainWindow, 'window.scroll.throttled')
    this.stopListening(this.mainWindow, 'window.resize.debounced')
  },

  /**
   * При изменении размеров окна скидывается кэш параметров окна
   * и отслеживаемых элементов, вызывается метод для отслеживания видимости
   */
  onResize () {
    this.showListening(this.mainWindow.getScrollTop())
  },

  /**
   * При скролле или изменении размеров окна определяется
   * входят ли отслеживаемые элементы в поле видимости
   * @param {int} windowTop - верхняя позиция окна
   */
  showListening (windowTop) {
    // Вычисление параметров окна
    const $window = this.mainWindow.$el
    windowTop = isNumber(windowTop) ? windowTop : $window.scrollTop()
    const windowParams = this._getWindowParams(windowTop)
    /*
     * флаг попадания элемента в зону видимости (элементы не занимающие место
     * и полностью прозрачные не считаются попадающими во viewport)
     */
    const visible = this._checkElementIsVisible(windowParams)
    // устанавливаем флаг попадания во viewport
    this.viewPortModel.set({ in: visible })
  },

  /**
   * Проверка видимости элемента в окне браузера
   * @param {Object} windowParams Параметры окна
   * @returns {boolean} Виден ли элемент
   */
  _checkElementIsVisible (windowParams) {
    const elWithParents = this.$el.parents().add(this.$el)
    const opaqueFilter = (index, el) => el.style.opacity === 0
    const isOpaque = elWithParents.filter(opaqueFilter).length === 0,
      isVisible = this.$el.is(':visible')
    let isInViewport = false
    if (isOpaque && isVisible) {
      isInViewport = this._checkElementInViewPort(elWithParents, windowParams)
    }
    return isVisible && isOpaque && isInViewport
  },

  /**
   * Проверка элемента на нахождение во вьюпорте
   * @param {Array} elWithParents Массив родительских элементов вместе с самим элементом
   * @param {Object} windowParams Параметры окна
   * @returns {boolean}
   */
  _checkElementInViewPort (elWithParents, windowParams) {
    const elCache = this.cache.element || {},
      parentCache = this.cache.parent || {}
    let isChangeParentPosition = false,
      overflowParentParams = parentCache

    // Вычисление позиции элемента
    const elOffset = this.$el.offset() || { left: 0, top: 0 },
      isChangeElPosition = elOffset.top !== elCache.top || elOffset.left !== elCache.left
    const elParams = isChangeElPosition ? this._getElementParams(this.$el, elCache, elOffset, 'element') : elCache

    if (isChangeElPosition) {
      // Вычисление позиции родительского элемента с overflow
      const $parentWithOverflow = elWithParents.filter((index, el) => {
        const $parent = $(el)
        return $parent.css('overflow') === 'hidden'
          || $parent.css('overflow-x') === 'hidden'
          || $parent.css('overflow-y') === 'hidden'
      })
      if ($parentWithOverflow.length) {
        const lastParent = $parentWithOverflow.last(),
          parentOffset = lastParent.offset() || { left: 0, top: 0 }
        isChangeParentPosition = parentOffset.top !== parentCache.top || parentOffset.left !== parentCache.left
        if (isChangeParentPosition) {
          overflowParentParams = this._getElementParams(lastParent, parentCache, parentOffset, 'parent')
        }
      }
    }
    /*
     * координаты текущей области просмотра
     * c учётом наличия родителей с overflow: hidden
     */
    const viewport = this._getViewportParams(windowParams, overflowParentParams)
    // Считаем баннер просмотренным, если видно не менее 70% его ширины
    const minWidthToCountAsVisible = Math.floor(elParams.width) * 0.7

    return !(
      viewport.right <= elParams.left + minWidthToCountAsVisible
      || viewport.left >= elParams.right + minWidthToCountAsVisible
      || viewport.bottom <= elParams.top
      || viewport.top >= elParams.bottom
    )
  },

  /**
   * Получение новых параметров элемента
   * @param {jQuery} el Элемент
   * @param {Object} cache Текущие кэшированные данные элемента
   * @param {Object} offset Новое значение offset-а
   * @param {string} cacheName Наименование кэша 'element'|'parent'
   * @returns {{
   *   top: int,
   *   left: int,
   *   bottom: int,
   *   width: int,
   *   right: int,
   *   height: int
   * }}
   * @private
   */
  _getElementParams (el, cache, offset, cacheName) {
    const isParent = cacheName === 'parent'
    let height = cache && cache.height,
      width = cache && cache.width
    if (!height || !width) {
      height = isParent ? el.innerHeight() : el.outerHeight()
      width = isParent ? el.innerWidth() : el.outerWidth()
    }
    this.cache[cacheName] = this._getPositionParams(offset, height, width)

    return this.cache[cacheName]
  },

  /**
   * Получение новых параметров окна
   * @param {int} windowTop Количество прокрученных пикселей
   * @returns {{
   *   top: int,
   *   left: int,
   *   bottom: int,
   *   width: int,
   *   right: int,
   *   height: int
   * }}
   * @private
   */
  _getWindowParams (windowTop) {
    const offset = {
        top: windowTop,
        left: this.mainWindow.$el.scrollLeft(),
      },
      height = this.mainWindow.getHeight(),
      width = this.mainWindow.getWidth()

    return this._getPositionParams(offset, height, width)
  },

  /**
   * Расчет параметров
   * @param {Object} offset Значение offset-а
   * @param {int} height Значение высоты
   * @param {int} width Значение ширины
   * @returns {{
   *   top: int,
   *   left: int,
   *   bottom: int,
   *   width: int,
   *   right: int,
   *   height: int
   * }}
   * @private
   */
  _getPositionParams (offset, height, width) {
    return {
      top: offset.top,
      left: offset.left,
      right: offset.left + width,
      bottom: offset.top + height,
      height: height,
      width: width,
    }
  },

  /**
   * Получение параметров вьюпорта
   * @param {Object} windowParams Параметры окна
   * @param {Object} parentParams Параметры родительского элемента с overflow: hidden
   * @returns {{top: int, left: int, right: int, bottom: int}}
   * @private
   */
  _getViewportParams (windowParams, parentParams) {
    // координаты текущей области просмотра
    let viewport = windowParams

    if (parentParams) {
      const intersectionBox = {
        left: Math.max(viewport.left, parentParams.left),
        top: Math.max(viewport.top, parentParams.top),
        right: Math.min(viewport.right, parentParams.right),
        bottom: Math.min(viewport.bottom, parentParams.bottom),
      }
      if (
        intersectionBox.left < intersectionBox.right
        && intersectionBox.top < intersectionBox.bottom
      ) {
        viewport = intersectionBox
      }
    }
    this.cache.viewport = viewport

    return viewport
  },

  /**
   * Меняет статус видимости.
   * @param {Event} e
   */
  changeViewport (e) {
    if (e.get('in')) {
      // попал во viewport
      this.inViewPort()
    } else {
      // вышел из viewport
      this.outViewPort()
    }
  },
}
